import {
  Platform,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { WEB_MAX_CONTENT_WIDTH } from '../../../theme/webLayoutWidths';
import React, { useCallback, useMemo } from 'react';
import {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetFooter,
  BottomSheetFooterProps,
  BottomSheetModal,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { v, s } from '../../../utils/libs';
import { theme } from '../../../theme';
import {
  shouldUseExpandedLayout,
  shouldUseWebLayout,
  shouldUseWidthConstraint,
} from '../../../utils/layoutUtils';
import { applyExpandedLayoutSnapBoost } from '../../../utils/bottomSheetSnapPoints';

interface CustomBottomSheetProps {
  bottomSheetModalRef: React.RefObject<BottomSheetModal | null>;
  snapPoints: string[];
  enableDynamicSizing?: boolean;
  maxDynamicContentSize?: number;
  handleSheetChanges: (index: number) => void;
  children?: React.ReactNode;
  // Optional fixed areas rendered outside the scrollable content
  header?: React.ReactNode;
  footer?: React.ReactNode;
  // When true, do not wrap children with BottomSheetScrollView
  disableDefaultScroll?: boolean;
  // When true, constrains width (maxWidth: WEB_MAX_CONTENT_WIDTH) on web and native tablet landscape
  constrainWidth?: boolean;
  /** When true, skip the 20% expanded-layout snap boost (default: boost when shouldUseExpandedLayout). */
  disableSnapBoost?: boolean;
}
const CustomBottomSheet = ({
  bottomSheetModalRef,
  snapPoints,
  enableDynamicSizing,
  maxDynamicContentSize,
  handleSheetChanges,
  children,
  header,
  footer,
  disableDefaultScroll,
  constrainWidth,
  disableSnapBoost,
}: CustomBottomSheetProps) => {
  const insets = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();
  const applyConstrainedWidth = useMemo(
    () =>
      Boolean(
        shouldUseWidthConstraint(width, height) && (constrainWidth ?? true),
      ),
    [constrainWidth, width, height],
  );

  const resolvedSnapPoints = useMemo(() => {
    if (disableSnapBoost || !shouldUseExpandedLayout(width, height)) {
      return snapPoints;
    }
    return applyExpandedLayoutSnapBoost(snapPoints);
  }, [snapPoints, width, height, disableSnapBoost]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => {
      // Use custom backdrop on web to avoid gesture-handler worklet warning
      if (Platform.OS === 'web') {
        const { animatedIndex, style } = props;
        const animatedStyle = useAnimatedStyle(() => ({
          opacity: interpolate(animatedIndex.value, [-1, 0], [0, 0.5]),
          display: animatedIndex.value < -0.5 ? 'none' : 'flex',
        }));
        return (
          <Animated.View
            style={[style, { backgroundColor: '#000' }, animatedStyle]}>
            <Pressable
              style={StyleSheet.absoluteFill}
              onPress={() => bottomSheetModalRef.current?.dismiss()}
            />
          </Animated.View>
        );
      }
      return (
        <BottomSheetBackdrop
          {...props}
          disappearsOnIndex={-1}
          appearsOnIndex={0}
        />
      );
    },
    [bottomSheetModalRef],
  );

  const renderFooter = useCallback(
    (props: BottomSheetFooterProps) => (
      <BottomSheetFooter {...props}>
        <View
          style={[
            styles.footerContainer,
            { paddingBottom: v(16) },
            applyConstrainedWidth && styles.webFooterContainer,
          ]}>
          {footer}
        </View>
      </BottomSheetFooter>
    ),
    [footer, insets.bottom, applyConstrainedWidth],
  );

  return (
    <BottomSheetModal
      ref={bottomSheetModalRef}
      index={0}
      bottomInset={insets.bottom}
      animateOnMount={true}
      enableDynamicSizing={enableDynamicSizing}
      maxDynamicContentSize={maxDynamicContentSize}
      snapPoints={resolvedSnapPoints}
      onChange={handleSheetChanges}
      enablePanDownToClose={true}
      enableOverDrag={false}
      android_keyboardInputMode="adjustPan"
      keyboardBehavior="extend"
      footerComponent={renderFooter}
      keyboardBlurBehavior="restore"
      backdropComponent={renderBackdrop}>
      <View style={styles.container}>
        {header ? (
          <View
            style={[
              styles.headerContainer,
              applyConstrainedWidth && styles.webContentWrapper,
            ]}>
            {header}
          </View>
        ) : null}

        <View
          style={[
            styles.contentWrapper,
            applyConstrainedWidth && styles.webContentWrapper,
          ]}>
          {disableDefaultScroll ? (
            <View style={styles.content}>{children}</View>
          ) : (
            <BottomSheetScrollView
              keyboardShouldPersistTaps="handled"
              style={styles.content}
              contentContainerStyle={styles.contentContainer}>
              {children}
            </BottomSheetScrollView>
          )}
        </View>
      </View>
    </BottomSheetModal>
  );
};

export default CustomBottomSheet;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  webContentWrapper: {
    maxWidth: WEB_MAX_CONTENT_WIDTH,
    alignSelf: 'center',
    width: '100%',
    paddingHorizontal: s(24),
  },
  contentWrapper: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    // extra space so scrollable content doesn't butt up against the footer
    paddingBottom: v(72),
  },
  headerContainer: {
    // justifyContent: 'center',
    // alignItems: 'center',
  },
  footerContainer: {
    backgroundColor: theme.colors.surface,
    paddingBottom: v(16),
    marginHorizontal: s(2),
  },
  webFooterContainer: {
    maxWidth: WEB_MAX_CONTENT_WIDTH,
    alignSelf: 'center',
    width: '100%',
    paddingHorizontal: s(24),
  },
});
